<template>
  <div ref="raiz" class="campana-wrap">
    <button class="campana" :aria-label="`Notificaciones (${contador} sin leer)`" @click="toggle">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" class="icono-campana"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
      <span v-if="contador > 0" class="badge">{{ contador > 99 ? '99+' : contador }}</span>
    </button>

    <transition name="desplegar">
      <div v-if="abierto" class="panel" role="menu">
        <div class="panel-cab">
          <b>Notificaciones</b>
          <button v-if="contador > 0" class="leer-todas" @click="leerTodas">Marcar todas leídas</button>
        </div>

        <div v-if="cargando" class="estado">Cargando…</div>
        <div v-else-if="!items.length" class="estado vacio">
          <span class="vacio-ico">✓</span>
          <p>Estás al día</p>
        </div>

        <ul v-else class="lista">
          <li
            v-for="n in items"
            :key="n.id_notificacion"
            class="item"
            :class="{ 'no-leida': !n.leida_en }"
            @click="abrir(n)"
          >
            <span class="sev" :class="'sev-' + n.severidad" :title="n.severidad">
              {{ n.severidad === 'critica' ? '⚠' : n.tipo === 'recordatorio' ? '📅' : '•' }}
            </span>
            <div class="cuerpo">
              <span class="titulo">{{ n.titulo }}</span>
              <span v-if="n.mensaje" class="mensaje">{{ n.mensaje }}</span>
              <span class="tiempo">{{ tiempoRelativo(n.created_at) }}</span>
            </div>
            <button class="btn-descartar" title="Descartar" @click.stop="descartar(n)">✕</button>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
/**
 * Campana de notificaciones con su contador de no leídas.
 *
 * «Leída» se marca con una fecha, no con un booleano, de modo que se conserva
 * cuándo se leyó.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import useAutenticar from '@/store/IngresoAutenticar'
import {
  getNotificacionesService,
  getContadorNotificacionesService,
  marcarLeidaService,
  marcarTodasLeidasService,
  type Notificacion
} from '@/services/NotificacionService'

const router = useRouter()
const auth = useAutenticar()

const abierto = ref(false)
const cargando = ref(false)
const contador = ref(0)
const items = ref<Notificacion[]>([])
const raiz = ref<HTMLElement | null>(null)
let intervalo: number | undefined

async function refrescarContador(): Promise<void> {
  if (!auth.autenticado) return
  contador.value = await getContadorNotificacionesService()
}

async function cargarLista(): Promise<void> {
  cargando.value = true
  const r = await getNotificacionesService()
  items.value = r.ok && r.data ? r.data.data : []
  cargando.value = false
}

function toggle(): void {
  abierto.value = !abierto.value
  if (abierto.value) cargarLista()
}

async function abrir(n: Notificacion): Promise<void> {
  if (!n.leida_en) {
    await marcarLeidaService(n.id_notificacion)
    n.leida_en = new Date().toISOString()
    contador.value = Math.max(0, contador.value - 1)
  }
  abierto.value = false
  if (n.enlace) router.push(n.enlace)
}

async function descartar(n: Notificacion): Promise<void> {
  if (!n.leida_en) {
    await marcarLeidaService(n.id_notificacion)
    contador.value = Math.max(0, contador.value - 1)
  }
  items.value = items.value.filter(i => i.id_notificacion !== n.id_notificacion)
}

async function leerTodas(): Promise<void> {
  await marcarTodasLeidasService()
  items.value = items.value.map(n => ({ ...n, leida_en: n.leida_en ?? new Date().toISOString() }))
  contador.value = 0
}

function tiempoRelativo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  return d === 1 ? 'ayer' : `hace ${d} días`
}

function clickFuera(e: MouseEvent): void {
  if (abierto.value && raiz.value && !raiz.value.contains(e.target as Node)) {
    abierto.value = false
  }
}

onMounted(() => {
  refrescarContador()
  intervalo = window.setInterval(refrescarContador, 60000)
  document.addEventListener('click', clickFuera)
})
onBeforeUnmount(() => {
  if (intervalo) window.clearInterval(intervalo)
  document.removeEventListener('click', clickFuera)
})
</script>

<style scoped>
.campana-wrap { position: relative; }
.campana { position: relative; border: none; background: none; cursor: pointer; color: #444; padding: 6px; border-radius: 50%; display: flex; }
.campana:hover { background: #eef5fb; color: #0e69a0; }
.icono-campana { width: 22px; height: 22px; }
.badge {
  position: absolute; top: 0; right: 0; background: #dc3545; color: #fff;
  font-size: 0.62rem; font-weight: 800; border-radius: 9px; min-width: 16px; height: 16px;
  display: flex; align-items: center; justify-content: center; padding: 0 4px; border: 2px solid #fff;
}

.panel {
  position: absolute; right: 0; top: calc(100% + 8px); width: 360px; max-width: 86vw;
  background: #fff; border: 1px solid var(--border-separator); border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg); z-index: 60; overflow: hidden;
}
.panel-cab { display: flex; justify-content: space-between; align-items: center; padding: 0.7rem 0.9rem; border-bottom: 1px solid var(--border-separator); font-size: 0.9rem; }
.leer-todas { border: none; background: none; color: #0e69a0; cursor: pointer; font-size: 0.76rem; font-weight: 600; }
.leer-todas:hover { text-decoration: underline; }

.estado { padding: 1.4rem; text-align: center; color: var(--text-muted); font-size: 0.85rem; }
.estado.vacio p { margin: 0.3rem 0 0; }
.vacio-ico { display: inline-flex; width: 34px; height: 34px; border-radius: 50%; background: #d4edda; color: #155724; align-items: center; justify-content: center; font-weight: 800; }

.lista { list-style: none; margin: 0; padding: 0; max-height: 380px; overflow-y: auto; }
.item { display: flex; gap: 0.6rem; padding: 0.65rem 0.9rem; cursor: pointer; border-bottom: 1px solid #f2f2f2; }
.item:hover { background: #f5f9fc; }
.item.no-leida { background: #eef5fb; }
.item.no-leida:hover { background: #e2eff8; }
.sev { flex-shrink: 0; width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; }
.sev-critica { background: #f8d7da; color: #721c24; }
.sev-alta { background: #fff3cd; color: #856404; }
.sev-media { background: #e8f4fd; color: #0e69a0; }
.sev-informativa { background: #f0f0f0; color: #666; }
.cuerpo { display: flex; flex-direction: column; min-width: 0; flex: 1; }

.btn-descartar {
  flex-shrink: 0;
  align-self: center;
  border: none;
  background: none;
  cursor: pointer;
  color: #bbb;
  font-size: 0.72rem;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.12s, background-color 0.12s;
}
.btn-descartar:hover { background: #e8e8e8; color: #555; }
/* El título lleva el proceso al que corresponde la alerta, así que se deja
   envolver: cortarlo en una línea era lo que hacía que cuatro notificaciones de
   procesos distintos se leyeran exactamente igual. */
.titulo {
  font-size: 0.82rem; font-weight: 600; color: #333;
  overflow-wrap: anywhere;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.mensaje { font-size: 0.76rem; color: var(--text-tertiary); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.tiempo { font-size: 0.7rem; color: var(--text-disabled); margin-top: 2px; }

.desplegar-enter-active, .desplegar-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.desplegar-enter-from, .desplegar-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
